import { useCallback, useRef } from 'react';

interface PendingField {
  value: any;
  timestamp: number;
}

const PENDING_EXPIRY_MS = 30000; // 30 seconds safety net

/**
 * Hook to track optimistic field updates and prevent stale data from overwriting
 * user-set values during the window between API call and webhook/DB update.
 * 
 * Usage:
 *   const { markPending, getFieldValue, clearExpired } = useOptimisticDeviceState();
 *   
 *   // Before API call:
 *   markPending('fieldName', newValue);
 *   
 *   // In useEffect([device]):
 *   clearExpired();
 *   setMyState(getFieldValue('fieldName', device.fieldName));
 */
export function useOptimisticDeviceState() {
  const pendingRef = useRef<Map<string, PendingField>>(new Map());

  const markPending = useCallback((field: string, value: any) => {
    pendingRef.current.set(field, { value, timestamp: Date.now() });
  }, []);

  const clearExpired = useCallback(() => {
    const now = Date.now();
    for (const [field, entry] of pendingRef.current) {
      if (now - entry.timestamp > PENDING_EXPIRY_MS) {
        pendingRef.current.delete(field);
      }
    }
  }, []);

  /**
   * Returns the optimistic value if the field is pending, otherwise the device value.
   * If the device value matches the pending value, clears it (webhook has arrived).
   */
  const getFieldValue = useCallback(<T>(field: string, deviceValue: T): T => {
    const pending = pendingRef.current.get(field);
    if (!pending) return deviceValue;

    // Check if expired
    if (Date.now() - pending.timestamp > PENDING_EXPIRY_MS) {
      pendingRef.current.delete(field);
      return deviceValue;
    }

    // If device value now matches pending, the webhook arrived — clear pending
    if (String(deviceValue) === String(pending.value)) {
      pendingRef.current.delete(field);
      return deviceValue;
    }

    // Still pending — return optimistic value
    return pending.value as T;
  }, []);

  const clearField = useCallback((field: string) => {
    pendingRef.current.delete(field);
  }, []);

  const clearAll = useCallback(() => {
    pendingRef.current.clear();
  }, []);

  return { markPending, getFieldValue, clearExpired, clearField, clearAll };
}
